import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {Spinner} from '../../../../misc/loader';
import {Box} from '@mui/material';
import * as STYLE_CONSTANTS from '../../../../../styles/styleConstants';
import {useLocation} from 'react-router-dom';
import base_url from '../../../../../baseUrls';
import StatusRenderer, {StatusRenderWithAdditionalData} from '../../../../../pieces/grids/statusRenderer';
import InspectionRequirementsRenderer from '../../../../../pieces/grids/inspectionRequirementsRenderer';
import LineBreakCellRenderer from '../../../../../pieces/grids/lineBreakCellRenderer';
import AiCallInProgressRenderer from '../../../../../pieces/grids/aiCallInProgressRenderer';
import RequirementFilter from '../../../../../pieces/grids/requirementFilter';
import MyPaginatedGrid from '../../../../../pieces/grids/paginatedGridWrapper';
import {MaximizableCellRenderer} from '../../../../../pieces/grids/MaximizableCellRenderer';
import {getInspectionColumns, inspectionApiTypes} from './inspectionsGridOptions';
import * as allConstants from '../../../../../constants/allConstants';
import {useAutocompleteOptions} from '../../../../../hooks/useAutompleteOptions';
import {useGetFilterList} from '../../../../../hooks/useGetFilterList';
import {useItemSelection} from '../../../../../hooks/useItemSelection';
import {inputTypes} from '../../../../../pieces/inputs/formInput';
import axios from 'axios';
import ApiUtils from '../../../../../utils/apiUtils';
//import {unwrapRequestResult} from '../../../../../utils/unwrapRequestResult';
import {toast} from 'react-toastify'
import {isContractor, isImpersonating} from '../../../../../permissions/utils';
import { Row } from '@syncfusion/ej2-charts';

/**
 * The AI queue views (In Progress, Rescheduled) are grouped by call date, one group per day, so a
 * call that was not placed on its day stays in view as overdue instead of dropping out (#4279).
 * Today's group opens by default. The server builds the groups (`groupByCallDate`).
 */
const NO_CALL_DATE_GROUP = 'none';

const callDateGroupLabel = (params) => {
  const data = params.node?.data ?? {};
  const key = data.callDateGroup ?? params.node?.key;
  if (!key || key === NO_CALL_DATE_GROUP) return 'No call date';
  const day = new Date(`${key}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  if (data.isToday) return `<b>Today</b> &middot; ${day}`;
  if (data.isOverdue) return `<span style="color:#c62828;font-weight:600">Overdue</span> &middot; ${day}`;
  return day;
};

const CALL_DATE_GROUP_COLUMN = {
  field: 'callDateGroup',
  colId: 'callDateGroup',
  headerName: 'Call Date Group',
  rowGroup: true,
  hide: true,
  lockVisible: true,
  suppressColumnsToolPanel: true,
  filter: false,
  sortable: false,
};

const CALL_DATE_SERVER_GROUP = {
  keyParam: 'callDateGroupKey',
  requestParams: {groupByCallDate: true},
  gridOptions: {
    groupDisplayType: 'groupRows',
    groupRowRendererParams: {innerRenderer: callDateGroupLabel, suppressCount: false},
    getChildCount: (data) => data?.childCount,
    isServerSideGroupOpenByDefault: (params) => Boolean(params.data?.isToday),
  },
};

const isCallDateGroupedView = (gridType) =>
  gridType === inspectionApiTypes.aiInProgress || gridType === inspectionApiTypes.aiRescheduled;

const AllInspectionsGrid = (props) => {

  //console.log('inside AllInspectionsGrid');
  //console.log(props);

  const viewController = props.viewController;
  const location = useLocation();
  const gridApiRef = useRef(null);
  const contextMenuActionsRef = useRef({});

  const [components] = useState({
    'statusRenderer': StatusRenderer,
    'statusRenderWithDate': StatusRenderWithAdditionalData,
    'MaximizableCellRenderer': MaximizableCellRenderer,
    'InspectionRequirementsRenderer': InspectionRequirementsRenderer,
    'LineBreakCellRenderer': LineBreakCellRenderer,
    'aiCallInProgressRenderer': AiCallInProgressRenderer,
    'requirementFilter': RequirementFilter,
    'booleanCellRenderer': (props) => <input type="checkbox" checked={props.value} readOnly />
  });
  

  // useEffect(() => {
  //   (async function getAllTagsAsync () {
  //       const getAllTags = async () => {
  //         tagNames = ['one', 'two', 'three'];
  //       }
  //       await getAllTags();
  //   })();
  // }, [])

  //useEffect( () => {
  //  getAllTags();
  //}, [])
    // Thanh 02/02/24
  //console.log('inside allInspectionsGrid');

  let scheduleAssigneeData = null;
  let followUpAssigneeData = null;

  const getSchedAssignees = async () => {

    const authToken =  await ApiUtils.getAccessToken();
    const url = `${base_url.api}users/get-schedule-assignees`;
    const newItems = await axios.get(url, {params: {}, headers: {Authorization: authToken}});
    
    return [{ value: 'UNASSIGNED', name: 'UNASSIGNED'}, ...newItems.data.data];
  }

  const getFollowUpAssignees = async () => {

    const authToken =  await ApiUtils.getAccessToken();
    const url = `${base_url.api}users/get-follow-up-assignees`;
    const newItems = await axios.get(url, {params: {}, headers: {Authorization: authToken}});
  
    return [{ value: 'UNASSIGNED', name: 'UNASSIGNED'}, ...newItems.data.data];
  }

  const submitItemSelection = useCallback(  (selectedText) => {
  })

  const scheduleItemData = {
    inputType: inputTypes.singleSelect,
    title: 'Schedule Assignee',
    defaultValue: '',
    fieldName: 'scheduleAssignee',
    apiName: 'cityInspection.schedAssignee',
    method: 'set-schedule-assignee',
    okAction: submitItemSelection,
  }  
  const followUpItemData = {
    inputType: inputTypes.singleSelect,
    title: 'Follow-up Assignee',
    defaultValue: '',
    fieldName: 'followUpAssignee',
    apiName: 'cityInspection.followUpAssignee',
    method: 'set-follow-up-assignee',
    okAction: submitItemSelection,
  };
  const tagNames = useGetFilterList({url : `${base_url.api}tags/`}).options;
  const {options: allCompaniesList, isLoading: companiesLoading} =
    useGetFilterList({url: `${base_url.api}companies/all-list`});
  /**
   * `null` only while the request is in flight, so the grid can hold its spinner until the
   * company filter values exist. `useGetFilterList` returns null both before the fetch and
   * after a failed one, so gating on `options` alone would hang forever when the endpoint
   * errors - a settled-but-failed fetch degrades to an empty list instead.
   */
  const userCompanies = useMemo(() => {
    if (companiesLoading) return null;
    if (!allCompaniesList) return [];
    const list = Array.isArray(allCompaniesList)
      ? allCompaniesList
      : (allCompaniesList.companies ?? allCompaniesList.data ?? []);
    return list.map((x) => (typeof x === 'string' ? x : x.name));
  }, [allCompaniesList, companiesLoading]);

  const scheduleItemSelection = useItemSelection(scheduleItemData);
  const followUpItemSelection = useItemSelection(followUpItemData);


  const jobStatusOptions = useAutocompleteOptions({field: 'jobStatuses'}).options;

  const handleDisplayJobDetails = (params) => {

    // Thanh 01/01/24
    //console.log('handle display job details');
    //console.log(params);

    // A call-date group header is not a job.
    if (params?.node?.group) return;
    props.viewJobHandler(params?.node?.data.id);
  };

  const handleAssignInspectionTo = (params) => {

    getSchedAssignees().then (x => {
      scheduleAssigneeData = x;
      scheduleItemSelection.showDialog(scheduleAssigneeData, params, 'cityInspection,schedAssignee');
    }); 
  }

  const handleFollowUpAssignee = (params) => {

    getFollowUpAssignees().then (x => {
      followUpAssigneeData = x;
      followUpItemSelection.showDialog(followUpAssigneeData, params, 'cityInspection,FollowUpAssignee');
    }); 
  }

  const pickJobFromRow = (row) => {
    if (!row || typeof row !== 'object') {
      return undefined;
    }

    //console.log('row', row);

    const JF = allConstants.JOB_FIELDS;
    const CF = allConstants.COMPANY_FIELDS;
    const INSP_FIELDS = allConstants.INSPECTION_FIELDS;

    const jobNumber = row[JF.JOB_NUMBER.api_name];
    //const cityInspection = row[INSP_FIELDS.INSPECTION.api_name];
    const inspectionJobId = row[JF.ID.api_name];

    if (jobNumber === null || jobNumber === undefined || String(jobNumber).trim() === '') {
      return undefined;
    }
    const ho = row[JF.GENERAL.HOMEOWNER.api_name] ?? {};
    const addr = row[JF.GENERAL.ADDRESS.api_name] ?? {};
    const company = row.company ?? {};

    const normalizePhone = (raw) => {
      const digits = String(raw ?? '').replace(/\D/g, '');
      if (!digits) {
        return '';
      }
      if (digits.length === 10) {
        return `+1${digits}`;
      }
      if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
      }
      const trimmed = String(raw ?? '').trim();
      if (trimmed.startsWith('+')) {
        return trimmed;
      }
      return `+${digits}`;
    };

    const line1 = addr[JF.GENERAL.JOBSITE_ADDRESS_LINE_1.api_name] ?? addr.line1 ?? '';
    const city = addr[JF.GENERAL.JOBSITE_ADDRESS_CITY.api_name] ?? addr.city ?? '';
    const state = addr[JF.GENERAL.JOBSITE_ADDRESS_STATE.api_name] ?? addr.state ?? '';
    const zip = addr[JF.GENERAL.JOBSITE_ADDRESS_ZIPCODE.api_name] ?? addr.zipCode ?? '';
    const stateZip = [state, zip].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
    const cityLine = [city, stateZip].filter(Boolean).join(', ').replace(/\s+/g, ' ').trim();
    const address = [String(line1).trim(), cityLine].filter(Boolean).join(', ').trim();

    const reasonForCall = 'final inspection scheduling';
    const combinedHoName = String(
      ho[JF.GENERAL.FIRST_NAME.api_name] ?? ho[JF.GENERAL.HO_NAME.api_name] ?? ho.firstName ?? ''
    )
      .trim()
      .replace(/\s+/g, ' ');
    let firstName = '';
    let lastName = '';
    if (combinedHoName) {
      const spaceIdx = combinedHoName.indexOf(' ');
      if (spaceIdx === -1) {
        firstName = combinedHoName;
      } else {
        firstName = combinedHoName.slice(0, spaceIdx);
        lastName = combinedHoName.slice(spaceIdx + 1).trim();
      }
    }

    const phone_number = normalizePhone(ho[JF.GENERAL.HO_PHONE.api_name] ?? ho.phoneNumber);
    const jobNumKey = String(jobNumber).trim();    
    const user_id = `${crypto.randomUUID()}`;
    const inspection = row[INSP_FIELDS.INSPECTION.api_name] ?? {};
    
    //console.log('inspection', inspection);
    //console.log('inspection[INSP_FIELDS.AI_CALL_ID.api_name]', inspection[INSP_FIELDS.AI_CALL_ID.api_name]);
    
    const aiCallId = row[INSP_FIELDS.AI_CALL_ID.api_name]?.toString() ?? undefined;
    
    const aiCallPriority = Number(
      inspection[INSP_FIELDS.AI_CALL_PRIORITY.api_name] ?? allConstants.AI_CALL_PRIORITIES.NORMAL.value,
    );

    return { 
      aiCallId: aiCallId,
      companyId: company.id,
      priority: aiCallPriority,
      call_seq: '',
      call_started: false,
      job_number: jobNumKey,
      inspection_job_id: inspectionJobId,
      state_name: String(state ?? '').trim(),
      call_data: {
        phone_number: phone_number,
        user_id,
        reason_for_call: reasonForCall,
        first_name: firstName,
        last_name: lastName,
        principal: String(company[CF.NAME.api_name] ?? '').trim(),
        job_id: jobNumKey,
        address,
      }
    };
  };
  const collectSelectedJobs = (params) => {
    const api = params?.api;
    if (!api) {
      return [];
    }
    const fromNodes = (api.getSelectedNodes?.() ?? []).map((n) => pickJobFromRow(n?.data));
    if (fromNodes.length > 0) {
      return fromNodes;
    }
    const fromRows = (api.getSelectedRows?.() ?? []).map((r) => pickJobFromRow(r));
    if (fromRows.length > 0) {
      return fromRows;
    }
    const one = pickJobFromRow(params?.node?.data);
    return one ? [one] : [];
  };

  const handleAssignToAiCalls = async (params) => {
    let jobs = [];
    try {
      jobs = collectSelectedJobs(params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[AssignToAiCalls] collectSelectedJobNumbers', e);
      toast.error('Could not read job information from the grid.');
      return;
    }
    if (jobs.length === 0) {
      toast.error('No jobs found. Select rows with the checkboxes, or right‑click a row that has a job.');
      return;
    }
    const authToken = await ApiUtils.getAccessToken();
    if (!authToken) {
      return;
    }

    const INSP_FIELDS = allConstants.INSPECTION_FIELDS;
    const JF = allConstants.JOB_FIELDS;
    const aiInProgressStatus = allConstants.AI_INSPECTION_STATUSES.IN_PROGRESS.value;
    const aiInProgressInspectionStatus = allConstants.INSPECTION_STATUSES.AI_IN_PROGRESS.value;
    const api = params?.api;

    try {
      for (const job of jobs) {
        await axios.post(
          `${base_url.api}ai/update-call-internal`,
          job,
          {headers: {Authorization: authToken, 'Content-Type': 'application/json'}}
        );

        if (api) {
          const jobNumber = String(job.job_number).trim();
          api.forEachNode((node) => {
            const row = node.data;
            if (!row || String(row[JF.JOB_NUMBER.api_name]).trim() !== jobNumber) {
              return;
            }
            const inspection = row[INSP_FIELDS.INSPECTION.api_name] ?? {};
            node.setData({
              ...row,
              [INSP_FIELDS.INSPECTION.api_name]: {
                ...inspection,
                [INSP_FIELDS.AI_INSPECTION_STATUS.api_name]: aiInProgressStatus,
                [INSP_FIELDS.INSPECTION_STATUS.api_name]: aiInProgressInspectionStatus,
              },
            });
          });
        }
      }
      api?.refreshCells({force: true});
      toast.success(`Submitted ${jobs.length} job(s) to AI calls.`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Assign to AI calls failed.';
      toast.error(msg);
    }
  };

  const handleSetCallAsClosedOut = async (params) => {
    let jobs = [];
    try {
      jobs = collectSelectedJobs(params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SetCallAsCompleted] collectSelectedJobNumbers', e);
      toast.error('Could not read job information from the grid.');
      return;
    }
    if (jobs.length === 0) {
      toast.error('No jobs found. Select rows with the checkboxes, or right‑click a row that has a job.');
      return;
    }
    const authToken = await ApiUtils.getAccessToken();
    if (!authToken) {
      return;
    }

    const INSP_FIELDS = allConstants.INSPECTION_FIELDS;
    const JF = allConstants.JOB_FIELDS;
    const closedOutAiStatus = allConstants.AI_INSPECTION_STATUSES.CLOSED_OUT.value;
    const aiClosedOutInspectionStatus = allConstants.INSPECTION_STATUSES.AI_CLOSED_OUT.value;
    const api = params?.api;

    try {
      for (const job of jobs) {
        const payload = {
          jobNumber: job.job_number,
          aiStatus: closedOutAiStatus,
        };
        await axios.post(
          `${base_url.api}ai/update-inspection-ai-info`,
          payload,
          {headers: {Authorization: authToken, 'Content-Type': 'application/json'}}
        );

        if (api) {
          const jobNumber = String(job.job_number).trim();
          api.forEachNode((node) => {
            const row = node.data;
            if (!row || String(row[JF.JOB_NUMBER.api_name]).trim() !== jobNumber) {
              return;
            }
            const inspection = row[INSP_FIELDS.INSPECTION.api_name] ?? {};
            node.setData({
              ...row,
              [INSP_FIELDS.INSPECTION.api_name]: {
                ...inspection,
                [INSP_FIELDS.AI_INSPECTION_STATUS.api_name]: closedOutAiStatus,
                [INSP_FIELDS.INSPECTION_STATUS.api_name]: aiClosedOutInspectionStatus,
              },
            });
          });
        }
      }
      api?.refreshCells({force: true});
      toast.success(`Marked ${jobs.length} call(s) as closed out.`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Set call as closed out failed.';
      toast.error(msg);
    }
  };

  const handleScheduleNextCall = async (params) => {
    let jobs = [];
    try {
      jobs = collectSelectedJobs(params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ScheduleNextCall] collectSelectedJobNumbers', e);
      toast.error('Could not read job information from the grid.');
      return;
    }
    if (jobs.length === 0) {
      toast.error('No jobs found. Select rows with the checkboxes, or right‑click a row that has a job.');
      return;
    }
    const authToken = await ApiUtils.getAccessToken();
    if (!authToken) {
      return;
    }
    try {
      for (const job of jobs) {
        const payload = {
          existingAiCallId: job.aiCallId,
          companyId: job.companyId,
        };

        const response = await fetch(
          `${base_url.api}ai/schedule-call-from-policy`,
          { method: 'POST',
            headers: {Accept: 'application/json','Content-Type': 'application/json', Authorization: authToken,},
            body: JSON.stringify(payload),
          }
        );
    
        let data = await response.json();

        console.log('data', data);
        console.log('data.newDate', data.newDate);

        // await axios.post(
        //   `${base_url.api}ai/schedule-call-from-policy`,
        //   payload,
        //   {headers: {Authorization: authToken, 'Content-Type': 'application/json'}}
        // );
        const INSP_FIELDS = allConstants.INSPECTION_FIELDS;
        const JF = allConstants.JOB_FIELDS;
        const api = params?.api;

        if (api) {
          const jobNumber = String(job.job_number).trim();
          api.forEachNode((node) => {
            const row = node.data;
            if (!row || String(row[JF.JOB_NUMBER.api_name]).trim() !== jobNumber) {
              return;
            }
            const inspection = row[INSP_FIELDS.INSPECTION.api_name] ?? {};
            node.setData({
              ...row,
              [INSP_FIELDS.INSPECTION.api_name]: {
                ...inspection,
                [INSP_FIELDS.AI_INSPECTION_STATUS.api_name]: allConstants.AI_INSPECTION_STATUSES.IN_PROGRESS.value,
                [INSP_FIELDS.INSPECTION_STATUS.api_name]: allConstants.INSPECTION_STATUSES.AI_IN_PROGRESS.value,
                [INSP_FIELDS.AI_CALL_DATE.api_name]: data.data.newDate,
              },
            });
          });
        }
      }

      toast.success(`Scheduled next call for ${jobs.length} job(s).`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Schedule next call failed.';
      toast.error(msg);
    }
  };

  const handleRemoveFromAiCall = async (params) => {
    let jobs = [];
    try {
      jobs = collectSelectedJobs(params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[RemoveFromAiCall] collectSelectedJobNumbers', e);
      toast.error('Could not read job information from the grid.');
      return;
    }
    if (jobs.length === 0) {
      toast.error('No jobs found. Select rows with the checkboxes, or right‑click a row that has a job.');
      return;
    }
    const authToken = await ApiUtils.getAccessToken();
    if (!authToken) {
      return;
    }
    try {
      for (const job of jobs) {
        const payload = {
          jobNumber: job.job_number,
          aiStatus: 'Not Allowed',
          aiCallId: job.aiCallId,
        };
        await axios.post(
          `${base_url.api}ai/update-inspection-ai-info`,
          payload,
          {headers: {Authorization: authToken, 'Content-Type': 'application/json'}}
        );
      }
      toast.success(`Removed ${jobs.length} job(s) from AI call(s).`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Remove from AI call failed.';
      toast.error(msg);
    }
  };

  const handleSetAiCallPriority = async (params, priorityValue) => {
    let jobs = [];
    try {
      jobs = collectSelectedJobs(params);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SetAiCallPriority] collectSelectedJobNumbers', e);
      toast.error('Could not read job information from the grid.');
      return;
    }
    if (jobs.length === 0) {
      toast.error('No jobs found. Select rows with the checkboxes, or right‑click a row that has a job.');
      return;
    }
    const authToken = await ApiUtils.getAccessToken();
    if (!authToken) {
      return;
    }

    const INSP_FIELDS = allConstants.INSPECTION_FIELDS;
    const JF = allConstants.JOB_FIELDS;
    const priority = Number(priorityValue);
    const api = params?.api;

    try {
      for (const job of jobs) {
        const payload = {
          jobNumber: job.job_number,
          [INSP_FIELDS.AI_CALL_PRIORITY.api_name]: priority,
          aiCallId: job.aiCallId,
        };
        await axios.post(
          `${base_url.api}ai/update-inspection-ai-info`,
          payload,
          {headers: {Authorization: authToken, 'Content-Type': 'application/json'}}
        );

        if (api) {
          const jobNumber = String(job.job_number).trim();
          api.forEachNode((node) => {
            const row = node.data;
            if (!row || String(row[JF.JOB_NUMBER.api_name]).trim() !== jobNumber) {
              return;
            }
            const inspection = row[INSP_FIELDS.INSPECTION.api_name] ?? {};
            node.setData({
              ...row,
              [INSP_FIELDS.INSPECTION.api_name]: {
                ...inspection,
                [INSP_FIELDS.AI_CALL_PRIORITY.api_name]: priority,
              },
            });
          });
        }
      }
      api?.refreshCells({force: true});
      toast.success(`Updated AI call priority for ${jobs.length} job(s).`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Set AI call priority failed.';
      toast.error(msg);
    }
  };

  const handleUpdateCallHoCount = async (params) => {

    //console.log('inside handle update call ho count');

    const authToken = await ApiUtils.getAccessToken();
    if(!authToken) {
      return;
    }
    //  const url = `${base_url.api}ipermit/jobs/city-inspection/set-schedule-assignee`;
    
    const response = await fetch(
      `${base_url.api}ipermit/jobs/city-inspection/update-call-ho-count`,
      { method: 'POST',
        headers: {Accept: 'application/json','Content-Type': 'application/json', Authorization: authToken,},
        body: JSON.stringify({jobId: params?.node?.data[allConstants.JOB_FIELDS.ID.api_name]}),
      }
    );

    let data = await response.json();

    //console.log('result from update call count');
    // console.log(data);

    if (data.status === 'success') {
      toast.success('Success', {
        toastId: `success_update_call_ho_count`
      });
    }
    else {
      toast.success('Error on updating howwner call count and last call date', {
        toastId: `success_update_call_ho_count`
      });
    }

  
  }

  contextMenuActionsRef.current = {
    handleAssignInspectionTo,
    handleFollowUpAssignee,
    handleAssignToAiCalls,
    handleSetCallAsClosedOut,
    handleScheduleNextCall,
    handleRemoveFromAiCall,
    handleSetAiCallPriority,
    handleDisplayJobDetails,
    handleUpdateCallHoCount,
    viewController,
  };

  const aiCallPrioritySubMenuItems = useMemo(
    () => Object.values(allConstants.AI_CALL_PRIORITIES)
      .sort((a, b) => a.showOrder - b.showOrder)
      .map((priority) => ({
        name: priority.display,
        value: priority.value,
      })),
    [],
  );

  const getContextMenuItems = useCallback((params) => {
    // A call-date group header has no inspection to act on.
    if (params?.node?.group) return [];
    const a = contextMenuActionsRef.current;
    const assignToAiCallsItem = {
      name: 'Assign to AI Calls',
      cssClasses: ['greenFont', 'bold'],
      disabled: false,
      action: () => { void a.handleAssignToAiCalls(params); },
    };
    const scheduleNextCallItem = {
      name: 'Schedule Next AI Call',
      cssClasses: ['greenFont', 'bold'],
      disabled: false,
      action: () => { void a.handleScheduleNextCall(params); },
    };
    const removeFromAiCallItem = {
      name: 'Remove From AI Call',
      cssClasses: ['greenFont', 'bold'],
      disabled: false,
      action: () => { void a.handleRemoveFromAiCall(params); },
    };
    const setCallAsClosedOutItem = {
      name: 'Close Out AI Call',
      cssClasses: ['greenFont', 'bold'],
      disabled: false,
      action: () => { void a.handleSetCallAsClosedOut(params); },
    };
    const setAiCallPriorityItem = {
      name: 'Set AI Call Priority',
      cssClasses: ['greenFont', 'bold'],
      subMenu: aiCallPrioritySubMenuItems.map((priority) => ({
        name: priority.name,
        action: () => { void a.handleSetAiCallPriority(params, priority.value); },
      })),
    };
    const standard = [
      ...(props.gridType === inspectionApiTypes.aiReady ? [assignToAiCallsItem] : []),
      ...[scheduleNextCallItem, removeFromAiCallItem, setCallAsClosedOutItem, setAiCallPriorityItem],
      'separator',
      // ...((props.gridType === inspectionApiTypes.aiInProgress
      //   || props.gridType === inspectionApiTypes.aiNeedToVerify)
      //   ? [setCallAsClosedOutItem, scheduleNextCallItem, removeFromAiCallItem]
      //   : []),
      {
        name: 'Assign Inspection To',
        cssClasses: ['greenFont', 'bold'],
        disabled: false,
        action: () => { a.handleAssignInspectionTo(params); },
      },
      {
        name: 'Assign Follow Up To',
        cssClasses: ['greenFont', 'bold'],
        disabled: false,
        action: () => { a.handleFollowUpAssignee(params); },
      },
      {
        name: 'Show Job Details',
        cssClasses: ['blueFont', 'bold'],
        disabled: a.viewController.selectedMultipleRows(),
        action: () => { a.handleDisplayJobDetails(params); },
      },
      'separator',
      {
        name: 'Update Homeowner Call Count and Last Call Date',
        cssClasses: ['blueFont', 'bold'],
        disabled: false,
        action: () => { a.handleUpdateCallHoCount(params); },
      },
      'separator',
      {
        name: 'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel({onlySelected: true})
      },
      'copy',
    ];

    return [...standard];
  }, [props.gridType, aiCallPrioritySubMenuItems]);

  const columnDefs = useMemo(()=> {

    //console.log("viewController.userRole inside columnDefs => ", viewController.userRole);
    
    if (!jobStatusOptions || !tagNames || !userCompanies) {
      return;
    }
    //console.log('inside columndefs - tagnames');
    //console.log(tagNames);

    //console.log('about to call getinspectioncolumns');

    const columns = getInspectionColumns(handleDisplayJobDetails, 'MaximizableCellRenderer',
      {...jobStatusOptions}, viewController.userRole, tagNames?.tags.map((x) => x.tagName), userCompanies,
      isImpersonating(viewController.userRole, viewController.companyId),
      viewController.user?.impersonateUserId);

    const noteColumns = [];

    if (viewController.userRole === allConstants.ROLES.IPERMIT_USER ||
        viewController.userRole === allConstants.ROLES.SYSTEM_ADMIN ||
        viewController.userRole === allConstants.ROLES.IPERMIT_ADMIN) {
        noteColumns.push(columns.lastInspNoteDate);
        noteColumns.push(columns.lastInspNote);
        noteColumns.push(columns.lastInspNoteCreatedBy);
    }

    if (props.gridType === inspectionApiTypes.aiAll || props.gridType === inspectionApiTypes.aiReady
       || props.gridType === inspectionApiTypes.aiInProgress || props.gridType === inspectionApiTypes.aiRescheduled
       || props.gridType === inspectionApiTypes.aiNeedToVerify
       || props.gridType === inspectionApiTypes.aiClosedOut) {
        return [
          ...(isCallDateGroupedView(props.gridType) ? [CALL_DATE_GROUP_COLUMN] : []),
          columns.id,
          columns.aiCallInProgress,
          columns.jobNumber,
          columns.aiCallPriority,
          columns.aiCallDate,
          columns.aiCallAnswer,
          columns.aiStatus,
          columns.aiResult,
          //columns.aiAnswer,
          columns.hoName,
          columns.address,
          columns.tags,
          columns.tagsReminderDate,
          columns.permitAssignee,
          columns.company,
          columns.companyName, 
          columns.jobType,
          columns.assignedCity,
          columns.requirements,
          columns.cityType,
          columns.permitStatus,
          columns.permitStatusChangeDate,
          columns.hersStatus,
          columns.hersStatusChangeDate,
          columns.schedulingStatus,
          columns.schedulingStatusChangeDate,
          columns.mailingStatus,
          columns.mailingStatusChangeDate,
          columns.lastHoCallDate,
          columns.callHoCount,
          columns.inspectionScheduledDate,
          columns.inspectionResult,
          columns.inspectionStatus,
          columns.schedReason,
          columns.failedCount,
          ...noteColumns,
          columns.permitExpiredDate,
          columns.aiCallId,
        ]
    }

    if (props.gridType === inspectionApiTypes.pending) {
      return [
        columns.id,
        columns.jobNumber,
        columns.acceptAiCall,
        columns.duplicateOf,
        columns.address,
        columns.state,
        columns.tags,
        columns.tagsReminderDate,
        columns.permitAssignee,
        columns.schedAssignee,
        columns.followUpAssignee,
        columns.inspectionStatusChangeDate, 
        columns.company,
        columns.companyName, 
        columns.jobType,
        columns.assignedCity,
        //columns.needsLadder,
        columns.requirements,
        columns.cityType,
        columns.permitStatus,
        columns.permitStatusChangeDate,
        columns.hersStatus,
        columns.hersStatusChangeDate,
        columns.schedulingStatus,
        columns.schedulingStatusChangeDate,
        columns.mailingStatus,
        columns.mailingStatusChangeDate,
        columns.lastHoCallDate,
        columns.callHoCount,
        columns.inspectionScheduledDate,
        columns.inspectionResult,
        columns.inspectionStatus,
        ...((isContractor(viewController.userRole) || isImpersonating(viewController.userRole, viewController.companyId)) ? [] : [columns.aiCallAnswer]),
        columns.schedReason,
        columns.failedCount,
        ...noteColumns,
        columns.permitExpiredDate,
      ];
    }
    if(props.gridType === inspectionApiTypes.hold || props.gridType === inspectionApiTypes.completed) {
      return [
        columns.id,
        columns.jobNumber,
        columns.duplicateOf,
        columns.address,
        columns.state,
        columns.tags,
        columns.tagsReminderDate,
        columns.permitAssignee,
        columns.schedAssignee,
        columns.followUpAssignee,
        columns.inspectionStatusChangeDate, 
        columns.company,
        columns.companyName,
        columns.jobType,
        columns.assignedCity,
        //columns.needsLadder,
        columns.requirements,
        columns.permitStatus,
        columns.permitStatusChangeDate,
        columns.hersStatus,
        columns.hersStatusChangeDate,
        columns.schedulingStatus,
        columns.schedulingStatusChangeDate,
        columns.mailingStatus,
        columns.mailingStatusChangeDate,
        columns.inspectionScheduledDate,
        columns.inspectionResult,
        ...noteColumns,
        columns.permitExpiredDate,
      ];
    }

    if(props.gridType === inspectionApiTypes.failed) {
      return [
        columns.id,
        columns.jobNumberWithOutResults,
        columns.duplicateOf,
        columns.address,
        columns.state,
        columns.tags,
        columns.tagsReminderDate,
        columns.permitAssignee,
        columns.schedAssignee,
        columns.followUpAssignee,
        columns.inspectionStatusChangeDate,  
        columns.company,
        columns.companyName,
        columns.jobType,
        columns.hoName,
        columns.assignedCity,
        //columns.needsLadder,
        columns.requirements,
        columns.permitStatus,
        columns.permitStatusChangeDate,
        columns.hersStatus,
        columns.hersStatusChangeDate,
        columns.schedulingStatus,
        columns.schedulingStatusChangeDate,
        columns.mailingStatus,
        columns.mailingStatusChangeDate,
        columns.lastHoCallDate,
        columns.callHoCount,
        columns.inspectionStatus,
        columns.schedReason,
        columns.failedCount,
        columns.inspectionScheduledDate,
        ...noteColumns,
        columns.permitExpiredDate,
      ];
    }

    if(props.gridType === inspectionApiTypes.report) {
      return [
        columns.id,
        columns.inspectionStatus,
        columns.schedReason,
        columns.failedCount,
        columns.jobNumber,
        columns.duplicateOf,
        columns.address,
        columns.state,
        columns.tags,
        columns.tagsReminderDate,
        columns.permitAssignee,
        columns.schedAssignee,
        columns.followUpAssignee,
        columns.inspectionStatusChangeDate, 
        columns.company,
        columns.companyName,
        columns.jobType,
        columns.hoName,
        columns.hoPhone,
        columns.assignedCity,
        //columns.needsLadder,
        columns.requirements,
        columns.cityType,
        columns.permitStatus,
        columns.permitStatusChangeDate,
        columns.hersStatus,
        columns.hersStatusChangeDate,
        columns.schedulingStatus,
        columns.schedulingStatusChangeDate,
        columns.mailingStatus,
        columns.mailingStatusChangeDate,
        columns.lastHoCallDate,
        columns.callHoCount,
        columns.inspectionScheduledDate,
        ...noteColumns,
        columns.permitExpiredDate,
      ];
    }

    const head = [
      columns.id,
      columns.jobNumber,
      columns.duplicateOf,
      columns.address,
      columns.state,
      columns.tags,
      columns.tagsReminderDate,
      columns.permitAssignee,
      columns.schedAssignee,
      columns.followUpAssignee,
      columns.inspectionStatusChangeDate,
      columns.company,
      columns.companyName,
      columns.jobType,
      columns.assignedCity,
      //columns.needsLadder,
      
      columns.requirements,
      columns.permitStatus,
      columns.permitStatusChangeDate,
      columns.hersStatus,
      columns.hersStatusChangeDate,
      columns.schedulingStatus,
      columns.schedulingStatusChangeDate,
      columns.mailingStatus,
      columns.mailingStatusChangeDate,
      columns.inspectionStatus,
      columns.schedReason,
      columns.failedCount,
      columns.lastHoCallDate,
      columns.callHoCount,
      columns.inspectionScheduledDate,
      columns.inspectionResult,
      ...noteColumns,
      columns.permitExpiredDate,
    ];

    if(props.gridType === inspectionApiTypes.manual) {
      //insert the manual inspection column at 3 position
      head.splice(3, 0, columns.createdTime);
    }

    return head;


  }, [jobStatusOptions, tagNames, userCompanies, props.gridType, viewController.userRole, viewController.companyId, viewController.user?.impersonateUserId]);


  const getFetchParams = (gridType)=> {
    const baseJobsUrl = `${base_url.api}jobs`;
    const ipermitBaseJobsUrl = `${base_url.api}ipermit/jobs`;
    const respKey1 = 'inspections';

    console.log("gridType => ", gridType);

    let url = baseJobsUrl;
    let params = {};
    if(gridType === inspectionApiTypes.previous) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-previous-inspections`;
    } else if (gridType === inspectionApiTypes.pending) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-with-status`;
      params.status = allConstants.INSPECTION_STATUSES.PENDING.value;
    } else if (gridType === inspectionApiTypes.aiAll) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = undefined;
    } else if (gridType === inspectionApiTypes.aiReady) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = allConstants.AI_INSPECTION_STATUSES.READY.value;
      // Actionable views hide past-dated rows; All and Closed Out stay historical.
      params.onlyDueCalls = true;
    } else if (gridType === inspectionApiTypes.aiInProgress) {
      // The whole call queue: a rescheduled inspection is still waiting for its next AI call.
      // Grouped by call date (CALL_DATE_SERVER_GROUP), so overdue days stay visible.
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = [
        allConstants.AI_INSPECTION_STATUSES.IN_PROGRESS.value,
        allConstants.AI_INSPECTION_STATUSES.RESCHEDULED.value,
      ];
    } else if (gridType === inspectionApiTypes.aiRescheduled) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = allConstants.AI_INSPECTION_STATUSES.RESCHEDULED.value;
    } else if (gridType === inspectionApiTypes.aiNeedToVerify) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = allConstants.AI_INSPECTION_STATUSES.VERIFY.value;
    } else if (gridType === inspectionApiTypes.aiClosedOut) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-ai-call`;
      params.status = allConstants.AI_INSPECTION_STATUSES.CLOSED_OUT.value;
    } else if(gridType === inspectionApiTypes.futureDate) {
        url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-with-status`;
        params.status = allConstants.INSPECTION_STATUSES.FUTURE_DATE.value;
    } else if(gridType === inspectionApiTypes.hold) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-with-status`;
      params.status = allConstants.INSPECTION_STATUSES.HOLD.value;
    }
    else if(gridType === inspectionApiTypes.completed) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-with-status`;
      params.status = allConstants.INSPECTION_STATUSES.COMPLETED.value;
    }
    else if(gridType === inspectionApiTypes.customerDeclined) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-inspections-with-status`;
      params.status = allConstants.INSPECTION_STATUSES.CUSTOMER_DECLINED.value;
    }
    else if(gridType === inspectionApiTypes.manual) {
      url = `${ipermitBaseJobsUrl}/city-inspection/get-manual-inspections`;
    }
    else if(gridType === inspectionApiTypes.failed) {
      url = `${url}/get-failed-inspections`;
    }
    else {
      url = `${url}/get-inspection-jobs`;
    }    
    return {url: url, respKey: respKey1, options: params};
  };

  const fetchParams = useMemo(() => getFetchParams(props.gridType), [props.gridType]);

  /** AI views include columns (e.g. aiStatus) not in older saved layouts; sharing `all_inspections` pushes new colIds to the end. */
  const inspectionsGridLsName = useMemo(() => {
    const aiGridTypes = [
      inspectionApiTypes.aiAll,
      inspectionApiTypes.aiReady,
      inspectionApiTypes.aiInProgress,
      inspectionApiTypes.aiRescheduled,
      inspectionApiTypes.aiNeedToVerify,
      inspectionApiTypes.aiCompleted,
    ];
    return aiGridTypes.includes(props.gridType) ? 'all_inspections_ai' : 'all_inspections';
  }, [props.gridType]);

  const filterRetryTimeoutsRef = useRef([]);

  const clearFilterRetryTimeouts = () => {
    filterRetryTimeoutsRef.current.forEach(clearTimeout);
    filterRetryTimeoutsRef.current = [];
  };

  const scheduleFilterRetry = (callback, delay = 100) => {
    const timeoutId = setTimeout(() => {
      filterRetryTimeoutsRef.current = filterRetryTimeoutsRef.current.filter((id) => id !== timeoutId);
      callback();
    }, delay);
    filterRetryTimeoutsRef.current.push(timeoutId);
  };

  // Set grid API reference when grid is registered
  const handleRegisterGrid = (params) => {
    gridApiRef.current = params.api;
    viewController.setGridToParent(params);
  };

  useEffect(() => {
    return () => {
      clearFilterRetryTimeouts();
    };
  }, []);

  // Check for statusName URL parameter and set filter for inspectionStatus column
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusName = searchParams.get('statusName');
    
    if (statusName) {
      // Construct the field name: parent + field.api_name
      // inspectionStatus column uses parent='cityInspection' and field=INSPECTION_STATUS (api_name: 'status')
      const inspectionStatusField = `cityInspection.${allConstants.INSPECTION_FIELDS.INSPECTION_STATUS.api_name}`;
      
      // Set filter on the inspectionStatus column - wait for grid to be ready
      const setFilter = () => {
        if (gridApiRef.current) {
          try {
            const filterComponent = gridApiRef.current.getFilterInstance(inspectionStatusField);
            if (filterComponent) {
              filterComponent.setModel({
                filterType: 'set',
                values: [statusName]
              });
              gridApiRef.current.onFilterChanged();
            }
          } catch (e) {
            console.log('Error setting inspectionStatus filter:', e);
          }
        } else {
          // Grid not ready yet, try again after a short delay
          scheduleFilterRetry(setFilter);
        }
      };
      
      setFilter();
    }
  }, [location.search]);

  /**
   * Must match the `columnDefs` memo's guard exactly. When it did not, a slow
   * `companies/all-list` (the impersonated/contractor branch walks the company hierarchy first)
   * let the grid mount with `columnDefs` undefined and then take a full column array once the
   * companies landed. Rebuilding columns on a live serverSide grid mid-block is what threw.
   */
  if(!jobStatusOptions || !tagNames || !userCompanies) return (
    <Box sx={props.containedLoader ? {
      // Inside a page with its own navigation (the AI dashboard): cover only the grid's
      // positioned container, so the page's tabs stay usable while this loads.
      position: 'absolute',
      inset: 0,
      background: 'white',
      zIndex: 2,
    } : {
      position: 'fixed',
      top: STYLE_CONSTANTS.HEIGHT.NAVBAR({isXS: false}),
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 9999,
    }}>
      <Spinner/>
    </Box>
  );
  return (
    <>
      {scheduleItemSelection.mainDialog()}
      {followUpItemSelection.mainDialog()}

      <MyPaginatedGrid columnDefs={columnDefs}
        // Grouped and flat grids differ in row model setup; remount rather than switch a live one.
        key={isCallDateGroupedView(props.gridType) ? 'call-date-grouped' : 'flat'}
        serverGroup={isCallDateGroupedView(props.gridType) ? CALL_DATE_SERVER_GROUP : undefined}
        components={components}
        rowHeight={40}
        colDefOverrides={{ autoHeight: false }}
        gridOptionsOverrides={{ animateRows: false, rowBuffer: 25 }}
        ls_name={inspectionsGridLsName}
        contextMenu={getContextMenuItems}
        onCellDoubleClicked={handleDisplayJobDetails}
        registerGrid={handleRegisterGrid}
        registerTempGrid={viewController.setTempGridToParent}    // Thanh 05/01/24
        fetchParams={fetchParams}
        pagination={props.pagination}
        onFetchFail={viewController.onFetchFail}
        defFilterParams={props.defFilterParams}
        additionalFilters={props.additionalFilters}
        refreshTimestamp={viewController.refreshTimestamp}
        globalFilters={props.globalFilters}
        globalSearchFilters={props.globalSearchFilters}

        //fieldsToFilterOnEnter={[allConstants.JOB_FIELDS.JOB_NUMBER]}
      />
    </>
  );
};

AllInspectionsGrid.propTypes = {
  pagination: PropTypes.instanceOf(Object).isRequired,
  gridType: PropTypes.oneOf(['hers', 'permits', 'all', 'previous', 'pending', 'manual', 'completed', 'hold', 'failed', 'report', 'ai ready', 'future date', 'report for export']),
  viewController: PropTypes.instanceOf(Object).isRequired,
  defFilterParams: PropTypes.instanceOf(Array),
  viewJobHandler: PropTypes.func.isRequired,
  additionalFilters: PropTypes.instanceOf(Object),
  /** Loader covers the nearest positioned ancestor instead of the whole window. */
  containedLoader: PropTypes.bool,
};

AllInspectionsGrid.defaultProps = {
  gridType: 'pending',
  defFilterParams: [],
  additionalFilters: {},
  containedLoader: false,
};
export default AllInspectionsGrid;

